import { jsPDF } from 'jspdf'
import { formatarDataBr } from '@/utils/date'
import { labelPeriodoSemana } from '@/utils/sprintGestao'

const MARGEM_X = 14
const LARGURA_TEXTO = 182
const RODAPE_Y = 285

function formatarDataHoraBr(date = new Date()) {
    const d = date instanceof Date ? date : new Date(date)
    const dia = String(d.getDate()).padStart(2, '0')
    const mes = String(d.getMonth() + 1).padStart(2, '0')
    const ano = d.getFullYear()
    const h = String(d.getHours()).padStart(2, '0')
    const min = String(d.getMinutes()).padStart(2, '0')
    return `${dia}/${mes}/${ano} ${h}:${min}`
}

function rotuloEntrega(item) {
    const partes = []
    if (item.pcm_codigo) partes.push(item.pcm_codigo)
    if (item.projeto_nome) partes.push(item.projeto_nome)
    const projeto = partes.join(' - ') || 'Projeto'
    const entrega = item.entrega_nome || `Pacote de trabalho #${item.entrega_id}`
    return `${projeto} · ${entrega}`
}

/**
 * Monta estrutura do relatório a partir das listas carregadas e alterações pendentes.
 */
export function montarDadosRelatorioGestaoEntregas({
    entregas = [],
    entregasSemFimPrevisto = [],
    entregasAtrasadas = [],
    entregasFixadas = [],
    idsValidados = [],
    repactuacoesPendentes = {},
}) {
    const mapa = new Map()
    for (const e of [...entregas, ...entregasSemFimPrevisto, ...entregasAtrasadas, ...entregasFixadas]) {
        mapa.set(Number(e.entrega_id), e)
    }

    const validacoes = idsValidados.map((id) => {
        const e = mapa.get(Number(id)) || { entrega_id: id }
        return {
            entrega_id: Number(id),
            entrega_nome: e.entrega_nome,
            projeto_nome: e.projeto_nome,
            pcm_codigo: e.pcm_codigo,
        }
    })

    const repactuacoes = Object.entries(repactuacoesPendentes).map(([id, dados]) => {
        const e = mapa.get(Number(id)) || { entrega_id: Number(id) }
        return {
            entrega_id: Number(id),
            entrega_nome: e.entrega_nome,
            projeto_nome: e.projeto_nome,
            pcm_codigo: e.pcm_codigo,
            fim_previsto_anterior: e.fim_previsto,
            dtFim: dados.dtFim,
            observacao_repactuacao: dados.observacao_repactuacao,
        }
    })

    return { validacoes, repactuacoes }
}

function novaPaginaSePreciso(doc, y, alturaNecessaria = 12) {
    if (y + alturaNecessaria > RODAPE_Y) {
        doc.addPage()
        return 20
    }
    return y
}

function escreverBloco(doc, y, titulo, linhas) {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(11)
    y = novaPaginaSePreciso(doc, y, 14)
    doc.text(titulo, MARGEM_X, y)
    y += 7

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)

    if (!linhas.length) {
        y = novaPaginaSePreciso(doc, y, 8)
        doc.setTextColor(100)
        doc.text('Nenhum registro nesta seção.', MARGEM_X, y)
        doc.setTextColor(0)
        return y + 8
    }

    for (const linha of linhas) {
        const texto = typeof linha === 'string' ? linha : linha.texto
        const blocos = doc.splitTextToSize(texto, LARGURA_TEXTO)
        y = novaPaginaSePreciso(doc, y, blocos.length * 5 + 4)
        doc.text(blocos, MARGEM_X, y)
        y += blocos.length * 5 + 3
    }

    return y + 4
}

/**
 * Gera e faz download do PDF com o resumo das alterações salvas.
 */
export function gerarRelatorioGestaoEntregasPdf({
    validacoes = [],
    repactuacoes = [],
    periodoInicio,
    periodoFim,
    usuarioNome = '',
    dataReferencia = new Date(),
}) {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' })
    const filename = `relatorio-gestao-entregas_${formatarDataHoraBr(dataReferencia).replace(/[/: ]/g, '-')}.pdf`
    popularDocumentoRelatorio(doc, {
        validacoes,
        repactuacoes,
        periodoInicio,
        periodoFim,
        usuarioNome,
        dataReferencia,
    })
    doc.save(filename)
}

function popularDocumentoRelatorio(doc, {
    validacoes = [],
    repactuacoes = [],
    periodoInicio,
    periodoFim,
    usuarioNome = '',
    dataReferencia = new Date(),
}) {
    let y = 18

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(16)
    doc.text('Relatório — Gestão de pacotes de trabalho', MARGEM_X, y)
    y += 10

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)

    const meta = [
        `Gerado em: ${formatarDataHoraBr(dataReferencia)}`,
        usuarioNome ? `Responsável: ${usuarioNome}` : null,
        periodoInicio && periodoFim
            ? `Período da consulta: ${labelPeriodoSemana(periodoInicio, periodoFim)}`
            : null,
        `Total: ${validacoes.length} validação(ões), ${repactuacoes.length} repactuação(ões)`,
    ].filter(Boolean)

    for (const linha of meta) {
        y = novaPaginaSePreciso(doc, y, 6)
        doc.text(linha, MARGEM_X, y)
        y += 6
    }

    y += 4

    const linhasValidacao = validacoes.map(
        (v, i) => `${i + 1}. ${rotuloEntrega(v)} foi validado pela gestão.`,
    )

    y = escreverBloco(doc, y, 'Pacotes de trabalho validados', linhasValidacao)

    const linhasRepactuacao = repactuacoes.map((r, i) => {
        const anterior = r.fim_previsto_anterior
            ? formatarDataBr(r.fim_previsto_anterior)
            : 'sem data'
        const nova = formatarDataBr(r.dtFim)
        const motivo = (r.observacao_repactuacao || '').trim() || '(sem observação)'
        return `${i + 1}. ${rotuloEntrega(r)} foi repactuado: fim previsto de ${anterior} para ${nova}. Motivação: ${motivo}`
    })

    y = escreverBloco(doc, y, 'Pacotes de trabalho repactuados', linhasRepactuacao)

    y = novaPaginaSePreciso(doc, y, 10)
    doc.setFontSize(9)
    doc.setTextColor(120)
    doc.text(
        'Documento gerado automaticamente após salvar alterações no painel Gestão de pacotes de trabalho.',
        MARGEM_X,
        y,
    )
}

export function gerarRelatorioGestaoEntregasPdfBlob({
    validacoes = [],
    repactuacoes = [],
    periodoInicio,
    periodoFim,
    usuarioNome = '',
    dataReferencia = new Date(),
}) {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' })
    popularDocumentoRelatorio(doc, {
        validacoes,
        repactuacoes,
        periodoInicio,
        periodoFim,
        usuarioNome,
        dataReferencia,
    })

    const blob = doc.output('blob')
    const filename = `relatorio-gestao-entregas_${formatarDataHoraBr(dataReferencia).replace(/[/: ]/g, '-')}.pdf`
    return { blob, filename }
}
